import React, { useCallback, useRef, useMemo, useEffect } from 'react';
import styles from './FullScreenWrapper.module.scss';
import FSIcon from '../../../assets/icons/fullscreen.svg?react';
import FSExitIcon from '../../../assets/icons/fullscreen_exit.svg?react';
import { useFullScreen } from '../../../hooks/useFullScreen';
import { useMediaQuery } from 'react-responsive';
import { usePresentationContext } from '@src/hooks/usePresentationContext';
import { useUser } from '@src/hooks/useUser';
import TranslationBadge from '@src/components/translation-provider/TranslationBadge';
import { logger } from '../../../utils/logger';

type Props = {
  children: (isFullScreen: boolean) => React.ReactNode;
};

// const isMobile = ("maxTouchPoints" in navigator && navigator.maxTouchPoints > 0) || ("orientation" in window) || /\b(BlackBerry|webOS|iPhone|IEMobile)\b/i.test(navigator.userAgent) || /\b(Android|Windows Phone|iPad|iPod)\b/i.test(navigator.userAgent);

function FullScreenWrapper(props: Props) {
  const { children } = props;
  const wrapperRef = useRef<HTMLDivElement>(null);
  const { open, close, fullScreen } = useFullScreen(wrapperRef);
  const { setIsPresenting } = usePresentationContext();
  const [user] = useUser();
  
  // Check if user is from Australia
  const isAUuser = useMemo(() => {
    return user?.id?.toLowerCase().startsWith("au");
  }, [user?.id]);
  
  // Set presentation state immediately when component mounts
  useEffect(() => {
    logger.info('PageFullScreenWrapper mounted - setting presentation mode to TRUE');
    setIsPresenting(true);
    
    return () => {
      logger.info('PageFullScreenWrapper unmounted - ending presentation');
      setIsPresenting(false);
    };
  }, [setIsPresenting]); // Include setIsPresenting in the dependency array

  const isPortrait = useMediaQuery({ query: '(orientation: portrait)' });
  const isMobile = useMediaQuery({ maxWidth: 915 });

  const enterFullScreen = useCallback(() => {
    // const videoFulLScreenButton = document.getElementsByClassName("vjs-fullscreen-control");
    // if (videoFulLScreenButton.length) (videoFulLScreenButton[0] as HTMLElement).click();
    // else open();
    open();
  }, [open]);

  const exitFullScreen = useCallback(() => {
    close();
  }, [close]);

  const fullScreenButtonBlock = useMemo(() => {
    // if (isMobile && fullScreen) return null;

    return (
      <div className={`${styles.actions} ${fullScreen ? styles.noPadding : ''}`}>
        {/* Translation Badge for full screen mode - hidden for Australian users */}
        {fullScreen && !isAUuser && (
          <TranslationBadge type="presentation" />
        )}
        
        {fullScreen ? <button onClick={exitFullScreen} className={`${styles.button} ${fullScreen ? styles.buttonFull : ''}`} type={'button'}>
          <div className={styles.iconWrapper}>
            <FSExitIcon/>
          </div>
          {(!isMobile || isPortrait) && <span className={styles.textWhite}>Exit full screen</span>}
        </button> : <button onClick={enterFullScreen} className={`${styles.button} ${fullScreen ? styles.buttonFull : ''}`} type={'button'}>
          <div className={styles.iconWrapper}>
            <FSIcon/>
          </div>
          {(!isMobile || isPortrait) && <span className={styles.text}>Open full screen</span>}
        </button>
        }
      </div>
    );
  }, [enterFullScreen, exitFullScreen, fullScreen, isMobile, isPortrait, isAUuser]);

  return (
    <div className={styles.wrapper} ref={wrapperRef}>
      {fullScreenButtonBlock}
      <div className={`${styles.content} ${fullScreen ? `${styles.isFull} ${isMobile ? styles.isMobileFull : ''}` : ''}`}>
        {children(fullScreen)}
      </div>
    </div>
  );
}

export default FullScreenWrapper;
